import type {
  ThemeOptions,
  ComponentsProps,
  ComponentsOverrides,
  ComponentsVariants
} from '@mui/material/styles';
import type { Theme } from '@ui/ThemeRegistry/theme.types';

import { BlockOwnerState, BlockVariants } from './Block.types';

interface LayoutConfig {
  [key: string]: { [breakpoint: string]: number };
}

function createLayoutConfig() {
  return {
    xs: 1,
    sm: 2,
    md: 2,
    lg: 2,
    xl: 2
  };
}

export const layoutConfig: LayoutConfig = {
  [BlockVariants.default]: createLayoutConfig(),
  [BlockVariants.mediaOnRight]: createLayoutConfig(),
  [BlockVariants.mediaOnLeft]: createLayoutConfig(),
  [BlockVariants.mediaOnRightFullBleed]: createLayoutConfig(),
  [BlockVariants.mediaOnLeftFullBleed]: createLayoutConfig(),
  [BlockVariants.mediaAbove]: createLayoutConfig(),
  [BlockVariants.mediaBelow]: createLayoutConfig(),
  [BlockVariants.text]: createLayoutConfig()
};

const defaultProps: ComponentsProps['Block'] = {
  variant: BlockVariants.default
};

const styleOverrides: ComponentsOverrides<Theme>['Block'] = {
  root: ({ theme, ownerState }) => ({
    ...theme.mixins.applyBackgroundColor({ ownerState, theme }),
    containerType: 'inline-size',
    position: 'relative',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: 0,

    ins: {
      textDecoration: 'none',
      color: 'var(--variant-highlight-color)'
    }
  }),

  introTextGrid: {
    paddingTop: 'var(--section-padding)'
  },

  introText: { gridColumn: 'start / end' },

  contentOuterGrid: ({ theme, ownerState }) => ({
    'margin': 'var(--grid-gap-double) 0',
    'rowGap': 'var(--grid-gap-double)',

    '> *': {
      gridColumnStart: 'auto'
    }
  }),

  overline: {
    textTransform: 'uppercase'
  },

  content: ({ theme, ownerState }) => ({
    'display': 'flex',
    'flexDirection': 'column',
    'gap': 'var(--grid-gap)',

    '& > *:last-child': {
      paddingBottom: 0,
      marginBottom: 0
    },

    ...(!!ownerState?.iconForListItems && {
      ':is(ol, ul)': {
        'padding': 'unset !important',

        'li': {
          marginLeft: '0 !important',

          p: {
            display: 'flex',
            textAlign: 'left',

            svg: {
              marginRight: theme.spacing(2),
              width: theme.spacing(2)
            }
          }
        },

        'li::marker': {
          content: 'none'
        }
      }
    })
  }),

  mainContentWrap: ({ theme, ownerState }) => ({
    display: 'flex',
    flexDirection: 'column',
    // alignSelf: ownerState?.supplementalContent ? 'flex-start' : 'center',
    gridColumnStart: 'start',
    gridColumnEnd: 'end',
    paddingTop: 'var(--grid-gap-double)',
    paddingBottom: 'var(--grid-gap-double)',
    justifyContent: 'center',

    ...(ownerState?.variant !== BlockVariants.mediaBelow &&
    ownerState?.variant !== BlockVariants.mediaAbove &&
    !ownerState?.fitImage
      ? {
          [theme.breakpoints.up('md')]: {
            paddingTop: 'var(--section-padding)',
            paddingBottom: 'var(--section-padding)'
          }
        }
      : {
          [theme.breakpoints.up('md')]: {
            paddingTop: 0,
            paddingBottom: 0
          }
        }),

    ...(!ownerState?.mediaItems && {
      gridRow: '1',
      gridColumnEnd: 'end !important'
    })

    // ...((ownerState?.variant === BlockVariants.mediaOnRight ||
    //   ownerState?.variant === BlockVariants.mediaOnRightFullBleed) && {
    //   [theme.containerBreakpoints.up('sm')]: {
    //     paddingRight: theme.spacing(3)
    //   },

    //   [theme.containerBreakpoints.up('lg')]: {
    //     paddingRight: theme.spacing(4)
    //   }
    // }),

    // ...((ownerState?.variant === BlockVariants.mediaOnLeft ||
    //   ownerState?.variant === BlockVariants.mediaOnLeftFullBleed) && {
    //   [theme.containerBreakpoints.up('sm')]: {
    //     paddingLeft: theme.spacing(3)
    //   },

    //   [theme.containerBreakpoints.up('lg')]: {
    //     paddingLeft: theme.spacing(4)
    //   }
    // })
  }),

  sideContentWrap: ({ ownerState, theme }) => ({
    'display': 'flex',
    // 'alignItems': ownerState?.supplementalContent ? 'flex-start' : 'center',
    // 'justifyContent': ownerState?.supplementalContent ? 'flex-start' : 'center',
    'flexDirection': 'column',
    'gridColumnStart': 'start',
    'gridColumnEnd': 'end',

    '[class*=media]': {
      objectFit: !!ownerState?.fitImage ? 'contain' : 'cover',
      borderRadius: theme.spacing(3),
      // aspectRatio: '5/4',

      [theme.containerBreakpoints.up('sm')]: {
        // aspectRatio: '1/1',
        borderRadius: theme.spacing(4)
      }

      // [theme.containerBreakpoints.up('lg')]: {
      //   aspectRatio: '5/4'
      // }
    },

    ...((ownerState?.variant === BlockVariants.mediaOnLeftFullBleed ||
      ownerState?.variant === BlockVariants.mediaOnRightFullBleed) && {
      '[class*=media]': {
        borderRadius: 'unset'
      }
    }),

    ...((ownerState?.variant === BlockVariants.default ||
      ownerState?.variant === BlockVariants.mediaAbove ||
      ownerState?.variant === BlockVariants.mediaBelow) && {
      '[class*=media]': {
        borderRadius: '32px',
        aspectRatio: '21/9',

        [theme.containerBreakpoints.only('xs')]: {
          aspectRatio: '1/1',
          borderRadius: '24px'
        }
      }
    }),

    ...(ownerState &&
      !ownerState.mediaItems && {
        [theme.containerBreakpoints.down('md')]: {
          gridRow: '2 !important'
        }
      }),

    'picture': {
      alignSelf: 'center',
      width: '100%',
      height: '100%',

      img: {
        width: '100%',
        height: '100%',
        objectFit: !!ownerState?.fitImage ? 'contain' : 'cover'
      }
    },

    ...(!!ownerState?.iconForListItems && {
      ':is(ol, ul)': {
        'padding': 'unset !important',

        'li': {
          marginLeft: '0 !important',

          p: {
            display: 'flex',
            textAlign: 'left',

            svg: {
              marginRight: theme.spacing(2),
              width: theme.spacing(2)
            }
          }
        },

        'li::marker': {
          content: 'none'
        }
      }
    })
  }),

  actionsWrap: ({ theme, ownerState }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--grid-gap)',

    ...(ownerState?.variant === BlockVariants.mediaBelow ||
    ownerState?.variant === BlockVariants.mediaAbove
      ? {
          marginTop: 'var(--grid-gap-half)'
        }
      : {
          marginTop: 'var(--grid-gap-double)'
        }),

    [theme.containerBreakpoints.up('sm')]: {
      flexDirection: 'row'
    }
  })
};

const createVariants = (theme: Theme, ownerState: BlockOwnerState): ComponentsVariants['Block'] => [
  {
    props: {
      variant: BlockVariants.default
    },
    style: {
      '[class*=contentOuterGrid]': {
        rowGap: 'var(--grid-gap)'
      },

      '[class*=mainContentWrap]': {
        gridRow: 2
      },

      '[class*=sideContentWrap]': {
        'gridRow': 1,

        '[class*=media]': {
          objectFit: !!ownerState?.fitImage ? 'contain' : 'cover'
        }
      },

      '[class*=content]': {
        alignItems: 'center',
        textAlign: 'center'
      },

      [theme.containerBreakpoints.up('sm')]: {
        '[class*=actionsWrap]': {
          alignSelf: 'center'
        }
      }
    }
  },
  {
    props: {
      variant: BlockVariants.mediaAbove
    },
    style: {
      '[class*=contentOuterGrid]': {
        rowGap: 'var(--grid-gap)'
      },

      '[class*=mainContentWrap]': {
        gridRow: 2,
        gap: 'var(--grid-gap)',

        [theme.containerBreakpoints.up('lg')]: {
          gridColumn: 'two-start/eleven-end !important'
        }
      },

      '[class*=sideContentWrap]': {
        'gridRow': 1,

        [theme.containerBreakpoints.up('lg')]: {
          gridColumn: 'two-start/eleven-end !important'
        },

        '[class*=media]': {
          objectFit: !!ownerState?.fitImage ? 'contain' : 'cover'
        }
      },

      '[class*=content]': {
        alignItems: 'center',
        textAlign: 'center'
      },

      [theme.containerBreakpoints.up('sm')]: {
        '[class*=actionsWrap]': {
          alignSelf: 'center'
        }
      }
    }
  },

  {
    props: {
      variant: BlockVariants.mediaBelow
    },
    style: {
      '[class*=contentOuterGrid]': {
        rowGap: 'var(--grid-gap-double)'
      },

      '[class*=mainContentWrap]': {
        gridRow: 1,
        gap: 'var(--grid-gap)',

        [theme.containerBreakpoints.up('lg')]: {
          gridColumn: 'two-start/eleven-end !important'
        }
      },

      '[class*=sideContentWrap]': {
        'gridRow': 2,

        [theme.containerBreakpoints.up('lg')]: {
          gridColumn: 'two-start/eleven-end !important'
        },

        '[class*=media]': {
          objectFit: !!ownerState?.fitImage ? 'contain' : 'cover'
        }
      },

      '[class*=content]': {
        alignItems: 'center',
        textAlign: 'center'
      },

      [theme.containerBreakpoints.up('sm')]: {
        '[class*=actionsWrap]': {
          alignSelf: 'center'
        }
      }
    }
  },

  {
    props: {
      variant: BlockVariants.text
    },
    style: {
      '[class*=sideContentWrap]': {
        '[class*=RichText-root]': {
          '& > *:not(:first-child)': {
            'minHeight': '1em',
            '&:not(:is(ul, ol, li))': {
              marginTop: '1em'
            }
          }
        }
      },
      '[class*=contentOuterGrid]': {
        [theme.containerBreakpoints.up('sm')]: {
          margin: 'calc(var(--grid-margin) + var(--grid-gap)) 0'
        },
        [theme.containerBreakpoints.up('xl')]: {
          margin: 'calc(var(--grid-margin-lg) + var(--grid-gap-lg)) 0'
        }
      }
    }
  },
  {
    props: {
      variant: BlockVariants.mediaOnRight
    },
    style: {
      '[class*=mainContentWrap]': {
        [theme.containerBreakpoints.up('sm')]: {
          gridRow: 1,
          gridColumnEnd: 'half'
        },

        [theme.containerBreakpoints.up('md')]: {
          gridColumnEnd: 'five-end'
        }
      },

      '[class*=sideContentWrap]': {
        gridRow: 1,

        [theme.containerBreakpoints.up('sm')]: {
          gridColumnStart: 'half'
        }
      }
    }
  },

  {
    props: {
      variant: BlockVariants.mediaOnRightFullBleed
    },
    style: {
      '[class*=contentOuterGrid]': {
        marginTop: 0,
        marginBottom: 0
      },

      '[class*=mainContentWrap]': {
        paddingTop: 'var(--grid-gap)',
        paddingBottom: 'var(--grid-gap)',

        [theme.containerBreakpoints.up('sm')]: {
          gridRow: 1,
          gridColumnEnd: 'half',
          marginRight: 'var(--grid-gap-half)'
        },

        [theme.containerBreakpoints.up('md')]: {
          gridColumnEnd: 'five-end'
        }
      },

      '[class*=sideContentWrap]': {
        gridRow: 1,

        gridColumnEnd: 'full-end',
        gridColumnStart: 'full-start',

        [theme.containerBreakpoints.up('sm')]: {
          gridColumnStart: 'half',
          marginLeft: 'calc(-1 * var(--grid-gap-half))',
          gridColumnEnd: 'full-end'
        }
      }
    }
  },

  {
    props: {
      variant: BlockVariants.mediaOnLeft
    },
    style: {
      '[class*=mainContentWrap]': {
        gridRow: 2,

        [theme.containerBreakpoints.up('sm')]: {
          gridRow: 1,
          gridColumnStart: 'half'
        },

        [theme.containerBreakpoints.up('md')]: {
          gridColumnStart: 'eight-start'
        }
      },

      '[class*=sideContentWrap]': {
        [theme.containerBreakpoints.up('sm')]: {
          gridColumnEnd: 'half'
        }
      }
    }
  },

  {
    props: {
      variant: BlockVariants.mediaOnLeftFullBleed
    },
    style: {
      '[class*=contentOuterGrid]': {
        marginTop: 0,
        marginBottom: 0
      },

      '[class*=mainContentWrap]': {
        gridRow: 2,
        paddingTop: 'var(--grid-gap)',
        paddingBottom: 'var(--grid-gap)',

        [theme.containerBreakpoints.up('sm')]: {
          gridRow: 1,
          gridColumnStart: 'half',
          marginLeft: 'var(--grid-gap-half)'
        },

        [theme.containerBreakpoints.up('md')]: {
          gridColumnStart: 'eight-start'
        }
      },

      '[class*=sideContentWrap]': {
        gridColumnEnd: 'full-end',
        gridColumnStart: 'full-start',

        [theme.containerBreakpoints.up('sm')]: {
          marginRight: 'calc(-1 * var(--grid-gap-half))',
          gridColumnEnd: 'half',
          gridColumnStart: 'full-start'
        }
      }
    }
  }
];

export const blockTheme = (theme: Theme, ownerState: BlockOwnerState): ThemeOptions => ({
  components: {
    Block: {
      defaultProps,
      styleOverrides,
      variants: createVariants(theme, ownerState)
    }
  }
});

export default blockTheme;
